import React, { useState, useRef, useEffect } from 'react';
import { 
  FileText, 
  Receipt,
  CreditCard,
  FileCheck,
  DollarSign,
  Truck,
  Plus,
  ChevronDown,
  ClipboardList
} from 'lucide-react';
import Button from '../../../components/ui/Button';
import Invoices from './components/Invoices';
import Quotes from './components/Quotes';
import Estimates from './components/Estimates';
import TaxInvoiceForm from './components/TaxInvoiceForm';

const Sales = () => {
  const [activeTab, setActiveTab] = useState('invoices');
  const [isCreateDropdownOpen, setIsCreateDropdownOpen] = useState(false);
  const [isInvoiceFormOpen, setIsInvoiceFormOpen] = useState(false);
  const [invoiceFormType, setInvoiceFormType] = useState('domestic-invoice');
  const [refreshKey, setRefreshKey] = useState(0);
  const dropdownRef = useRef(null);

  const tabs = [
    { id: 'invoices', label: 'Invoices', icon: FileText },
    { id: 'quotes', label: 'Quotes', icon: FileCheck },
    { id: 'estimates', label: 'Estimates', icon: ClipboardList },
    { id: 'bills-of-supply', label: 'Bills of Supply', icon: Receipt },
    { id: 'credit-notes', label: 'Credit Notes', icon: CreditCard },
    { id: 'receipts', label: 'Receipts', icon: Receipt },
    { id: 'receivables', label: 'Receivables', icon: DollarSign },
    { id: 'delivery-challans', label: 'Delivery Challans', icon: Truck }
  ];

  const createOptions = [
    { id: 'quote', label: 'Quote', icon: FileCheck },
    { id: 'estimate', label: 'Estimate', icon: FileCheck },
    { id: 'domestic-invoice', label: 'Domestic Invoice', icon: FileText },
    { id: 'international-invoice', label: 'International Invoice', icon: FileText },
    { id: 'bill-of-supply', label: 'Bill of Supply', icon: Receipt },
    { id: 'credit-note', label: 'Credit Note', icon: CreditCard },
    { id: 'receipt', label: 'Receipt', icon: Receipt },
    { id: 'delivery-challan', label: 'Delivery Challan', icon: Truck }
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsCreateDropdownOpen(false);
      }
    };

    if (isCreateDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCreateDropdownOpen]);

  const handleCreateOption = (optionId) => {
    setIsCreateDropdownOpen(false);
    // Open the form for all document types
    setInvoiceFormType(optionId);
    setIsInvoiceFormOpen(true);
  };

  const handleInvoiceFormSave = (savedData) => {
    setIsInvoiceFormOpen(false);
    // Refresh the active tab's list based on what was created
    if (invoiceFormType === 'quote' && activeTab === 'quotes') {
      setRefreshKey(prev => prev + 1);
    } else if (invoiceFormType === 'estimate' && activeTab === 'estimates') {
      setRefreshKey(prev => prev + 1);
    } else if ((invoiceFormType === 'domestic-invoice' || invoiceFormType === 'international-invoice') && activeTab === 'invoices') {
      // Refresh invoices if on invoices tab
      window.location.reload();
    } else if (invoiceFormType === 'bill-of-supply' && activeTab === 'bills-of-supply') {
      setRefreshKey(prev => prev + 1);
    } else if (invoiceFormType === 'credit-note' && activeTab === 'credit-notes') {
      setRefreshKey(prev => prev + 1);
    } else if (invoiceFormType === 'delivery-challan' && activeTab === 'delivery-challans') {
      setRefreshKey(prev => prev + 1);
    } else if (invoiceFormType === 'receipt' && activeTab === 'receipts') {
      setRefreshKey(prev => prev + 1);
    } else if (invoiceFormType === 'sales-register' && activeTab === 'sales-register') {
      setRefreshKey(prev => prev + 1);
    } else {
      // Refresh if we're on the relevant tab
      setRefreshKey(prev => prev + 1);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'invoices':
        return <Invoices key={refreshKey} />;

      case 'quotes':
        return <Quotes key={refreshKey} />;

      case 'estimates':
        return <Estimates key={refreshKey} />;

      case 'bills-of-supply':
        const BillsOfSupply = React.lazy(() => import('./components/BillsOfSupply'));
        return (
          <React.Suspense fallback={<div className="p-6">Loading bills of supply...</div>}>
            <BillsOfSupply />
          </React.Suspense>
        );

      case 'credit-notes':
        const CreditNotes = React.lazy(() => import('./components/CreditNotes'));
        return (
          <React.Suspense fallback={<div className="p-6">Loading credit notes...</div>}>
            <CreditNotes key={refreshKey} />
          </React.Suspense>
        );

      case 'receipts':
        const Receipts = React.lazy(() => import('./components/Receipts'));
        return (
          <React.Suspense fallback={<div className="p-6">Loading receipts...</div>}>
            <Receipts key={refreshKey} />
          </React.Suspense>
        );

      case 'sales-register':
        const SalesRegister = React.lazy(() => import('./components/SalesRegister'));
        return (
          <React.Suspense fallback={<div className="p-6">Loading sales register...</div>}>
            <SalesRegister key={refreshKey} />
          </React.Suspense>
        );

      case 'receivables':
        const Receivables = React.lazy(() => import('./components/Receivables'));
        return (
          <React.Suspense fallback={<div className="p-6">Loading receivables...</div>}>
            <Receivables />
          </React.Suspense>
        );

      case 'delivery-challans':
        const DeliveryChallans = React.lazy(() => import('./components/DeliveryChallans'));
        return (
          <React.Suspense fallback={<div className="p-6">Loading delivery challans...</div>}>
            <DeliveryChallans key={refreshKey} />
          </React.Suspense>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Create New Button */}
      <div className="flex justify-end">
        <div className="relative" ref={dropdownRef}>
          <Button
            onClick={() => setIsCreateDropdownOpen(!isCreateDropdownOpen)}
            className="flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Create New</span>
            <ChevronDown className={`w-4 h-4 transition-transform ${isCreateDropdownOpen ? 'rotate-180' : ''}`} />
          </Button>

          {isCreateDropdownOpen && (
            <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg border border-gray-200 z-50 overflow-y-auto max-h-96">
              <div className="py-1">
                {createOptions.map((option) => {
                  const IconComponent = option.icon;
                  return (
                    <button
                      key={option.id}
                      onClick={() => handleCreateOption(option.id)}
                      className="w-full flex items-center space-x-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                    >
                      <IconComponent className="w-4 h-4 text-gray-500" />
                      <span>{option.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6 overflow-x-auto">
            {tabs.map((tab) => {
              const IconComponent = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
                  }`}
                >
                  <IconComponent className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {renderTabContent()}
        </div>
      </div>

      {/* Tax Invoice Form Modal */}
      {isInvoiceFormOpen && (
        <TaxInvoiceForm
          isOpen={isInvoiceFormOpen}
          onClose={() => setIsInvoiceFormOpen(false)}
          onSave={handleInvoiceFormSave}
          invoiceType={invoiceFormType}
        />
      )}
    </div>
  );
};

export default Sales;

